import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { DataRoomActivityWindow, VisitorProfile, VisitorTimelineDay } from 'shared';

import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import { useLocale } from '../../../../hooks/use-locale.hook';
import { ApiService } from '../../../../services/index';
import { selectedCompanyStorage } from '../../../../storage/company/selected-company.util';
import { keepWithinScope } from './keep-within-scope.util';

/** The timeline's pager: five days more, or back to the first five. */
export type ActivityVisitorTimeline = {
  canShowLess: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  nextPageSize: number;
  showLess: () => void;
};

export type ActivityVisitor = {
  isError: boolean;
  isPending: boolean;
  isReloading: boolean;
  profile: undefined | VisitorProfile;
  retry: () => void;
  timeline: ActivityVisitorTimeline;
};

/** Days the timeline shows at a time; each "Load more" adds as many. */
export const VISITOR_TIMELINE_DAYS = 5;

// The most entries the server answers in one request; a busy day takes a few.
export const VISITOR_TIMELINE_BATCH = 100;

// The most requests one page makes. A bulk download logs an entry per file, so one click can fill
// a day with thousands; past this a page ends inside that day, and the next one carries on there.
export const VISITOR_TIMELINE_MAX_BATCHES = 5;

// One page of the timeline: its days (the last cut short only on a very busy day), and where in
// the window's entries the next page starts.
type TimelinePage = { nextSkip: number } & VisitorProfile;

const countEntries = (days: VisitorTimelineDay[]): number =>
  days.reduce((count, day) => count + day.entries.length, 0);

// Batches arrive newest first, so a day cut by a batch boundary is the last of one batch and the
// first of the next: its two halves join into one day.
const joinDays = (days: VisitorTimelineDay[]): VisitorTimelineDay[] =>
  days.reduce<VisitorTimelineDay[]>((joined, day) => {
    const last = joined[joined.length - 1];

    return last?.day === day.day
      ? [...joined.slice(0, -1), { day: day.day, entries: [...last.entries, ...day.entries] }]
      : [...joined, day];
  }, []);

/**
 * One visitor's profile — 20.1.4 §5 to §9.
 *
 * Addressed by the opaque key the row carried, never by the address. The timeline pages by days,
 * not entries: a page is five whole days with every entry they hold, "Load more" adds the next
 * five and "Show less" folds back to the first. The server pages by entries, so a page reads
 * batches until a sixth day begins — that is how it knows the fifth is complete — and the next page
 * starts at that sixth day. A day too busy to finish within the page's batches is cut where they
 * end, and the next page goes on from there. The header and cards are the first page's.
 */
export const useActivityVisitor = ({
  dataRoomId,
  visitorKey,
  window,
}: {
  dataRoomId?: string;
  visitorKey: string;
  window: DataRoomActivityWindow;
}): ActivityVisitor => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const queryClient = useQueryClient();
  const { messagesLocale } = useLocale();
  const queryKey = useMemo(
    () => [QueryKey.GET_DATA_ROOM_ACTIVITY_VISITOR, companyId, visitorKey, dataRoomId, window],
    [companyId, dataRoomId, visitorKey, window],
  );

  // When this query became the one shown; a failure older than that is the cache's.
  const shownSince = useMemo(() => ({ at: Date.now(), queryKey }), [queryKey]).at;

  const {
    data,
    errorUpdatedAt,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isFetchNextPageError,
    isPending,
    isPlaceholderData,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam: skip, signal }): Promise<TimelinePage> => {
      const batches: VisitorProfile[] = [];
      let days: VisitorTimelineDay[] = [];
      let read = skip;

      do {
        // A window or room changed mid-page: the batches still to come are for nobody.
        if (signal.aborted) throw new DOMException('The timeline page was abandoned', 'AbortError');

        const batch = await ApiService.get<VisitorProfile>(
          { endpoint: BackendRoute.DATA_ROOM_ACTIVITY, routePath: ['visitors', visitorKey] },
          {
            queryParams: {
              ...(dataRoomId ? { dataRoomId } : {}),
              skip: read,
              take: VISITOR_TIMELINE_BATCH,
              window,
            },
          },
        );

        batches.push(batch);
        days = joinDays([...days, ...batch.timeline]);
        read += countEntries(batch.timeline);

        if (!batch.timeline.length) break;
      } while (
        days.length <= VISITOR_TIMELINE_DAYS &&
        read < batches[0].timelineCount &&
        batches.length < VISITOR_TIMELINE_MAX_BATCHES
      );

      const timeline = days.slice(0, VISITOR_TIMELINE_DAYS);

      return { ...batches[0], nextSkip: skip + countEntries(timeline), timeline };
    },
    // A page that read nothing new ends the timeline, whatever the count still says.
    getNextPageParam: ({ nextSkip, timelineCount }, _pages, skip) =>
      nextSkip > skip && nextSkip < timelineCount ? nextSkip : undefined,
    initialPageParam: 0,
    placeholderData: keepWithinScope([
      QueryKey.GET_DATA_ROOM_ACTIVITY_VISITOR,
      companyId,
      visitorKey,
    ]),
  });

  // A later page failing keeps the entries already read on screen, so it is told instead — every
  // time it fails, not only the first, and never again for a failure the cache kept from before.
  useEffect(() => {
    if (isFetchNextPageError && errorUpdatedAt >= shownSince) {
      toast.error(messagesLocale.ACTIVITY_LOAD_FAILED);
    }
  }, [errorUpdatedAt, isFetchNextPageError, messagesLocale, shownSince]);

  const profile = useMemo((): undefined | VisitorProfile => {
    const pages = data?.pages;

    if (!pages?.length) return undefined;

    // Pages end on whole days, but the window moves on between "Load more"s: a page can begin
    // inside a day already shown, or repeat its last entries. Days are joined and every entry is
    // kept once, where it first appeared.
    const seen = new Set<string>();
    const timeline = joinDays(pages.flatMap((page) => page.timeline))
      .map((timelineDay) => ({
        ...timelineDay,
        entries: timelineDay.entries.filter(({ id }) => {
          if (seen.has(id)) return false;

          seen.add(id);

          return true;
        }),
      }))
      .filter(({ entries }) => entries.length > 0);

    return { ...pages[0], timeline };
  }, [data]);

  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  const showLess = useCallback(
    () =>
      queryClient.setQueryData<InfiniteData<TimelinePage, number>>(
        queryKey,
        (current) =>
          current && {
            pageParams: current.pageParams.slice(0, 1),
            pages: current.pages.slice(0, 1),
          },
      ),
    [queryClient, queryKey],
  );

  return {
    // The first page failed: there is no profile to show.
    isError: isError && !profile,
    isPending,
    isReloading: isPlaceholderData,
    profile,
    retry: refetch,
    timeline: {
      canShowLess: !hasNextPage && (data?.pages.length ?? 0) > 1,
      isLoadingMore: isFetchingNextPage,
      loadMore,
      nextPageSize: hasNextPage ? VISITOR_TIMELINE_DAYS : 0,
      showLess,
    },
  };
};
